'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Embedded security in sprint delivery',
    description:
      'TechSonar engineers join your standups, attend sprint planning, and review stories for security implications before development begins. Security is part of the definition of done, not a gate at the end.',
  },
  {
    tag: '[02]',
    title: 'Continuous pipeline security validation',
    description:
      'Every commit triggers security validation. SAST, SCA, secrets detection, and container scanning run in your pipeline. Findings are triaged in real time, not batched into a monthly report.',
  },
  {
    tag: '[03]',
    title: 'Ongoing monitoring and drift detection',
    description:
      'Security posture is monitored continuously. Configuration drift, new CVEs affecting your stack, and anomalous behaviour are detected and escalated, not discovered in the next annual review.',
  },
  {
    tag: '[04]',
    title: 'Security culture and capability building',
    description:
      'Embedded engineers transfer knowledge. Developers learn secure coding patterns, security champions are developed, and your team builds genuine security capability, not dependency on external consultants.',
  },
];

const outcomes = [
  { metric: 'Day 1', label: 'Embedded in your team from engagement start' },
  { metric: '365', label: 'Days of continuous security coverage per year' },
  { metric: '< 4h', label: 'Mean time to triage critical pipeline findings' },
  { metric: 'Zero', label: 'Security surprises at audit or incident time' },
];

const engagementModel = [
  {
    title: 'Fractional embedding',
    desc: 'A dedicated TechSonar engineer allocated to your team for a defined number of days per week. Attends standups, reviews PRs, joins sprint ceremonies. Scales with your delivery cadence.',
  },
  {
    title: 'Full-time embedding',
    desc: 'A senior TechSonar engineer working full-time within your engineering organisation. Owns the security programme, drives remediation, and builds internal capability.',
  },
  {
    title: 'Compliance window support',
    desc: 'Intensive embedding during SOC2 or ISO 27001 audit windows. Controls implemented, evidence collected, and your team supported through the audit process end-to-end.',
  },
  {
    title: 'DevSecOps acceleration',
    desc: 'Time-boxed engagement to establish your DevSecOps programme, pipeline tooling, security gates, monitoring, and process, then transition to your team with full knowledge transfer.',
  },
];

export default function DevSecOpsPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '700px', height: '700px', top: '-200px', left: '-150px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '10%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Continuous DevSecOps</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Continuous DevSecOps]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Embedded security<br />
              <span className="text-[#80edfa]">at engineering speed.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              We do not parachute in once a year. TechSonar engineers work as part of your team, in your sprints, your standups, your delivery process. Continuous pipeline hardening, ongoing monitoring, rapid remediation, and security reporting that gives leadership real visibility. Security that moves at the speed your business demands.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Discuss embedding options</a>
              <a href="#model" className="btn-secondary">Engagement models</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">How we work</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Inside your team, not alongside it
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                The difference between embedded DevSecOps and traditional security consulting is not just frequency, it is integration. TechSonar engineers become part of your engineering culture, not a vendor relationship.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section id="model" className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Engagement models</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Structured to fit how your organisation works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {engagementModel?.map((m, i) => (
              <div key={i} className="border border-[#222] bg-[#181b1f] overflow-hidden fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="h-1 w-full bg-gradient-to-r from-[#80edfa] to-transparent" />
                <div className="p-8">
                  <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{m?.title}</h3>
                  <p className="text-sm text-[#aaa] leading-relaxed">{m?.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What continuous means */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-1/2 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">What continuous means</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                Not point-in-time. Not fix-and-forget.
              </h2>
              <p className="text-[#aaa] text-base leading-relaxed mb-8">
                Most security engagements produce a report. The findings sit in a backlog. Six months later, half are unresolved. A year later, the same pen test is run again. TechSonar breaks this cycle, embedded engineers own remediation, track progress, and ensure your security posture improves continuously.
              </p>
              <ul className="flex flex-col gap-4">
                {[
                  'Daily pipeline security validation on every commit',
                  'Weekly security posture review with your engineering lead',
                  'Monthly executive reporting with meaningful metrics',
                  'Quarterly risk assessment and programme review',
                  'Continuous CVE monitoring against your specific stack',
                  'Immediate escalation for critical findings, not batched',
                ]?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg" alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#aaa]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-1/2 flex flex-col gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Senior engineers only</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Every TechSonar engineer embedded into your team is senior. No juniors. No graduates learning on your codebase. Engineers who have built and secured production systems at scale, and who can have a peer conversation with your most experienced developers.
                </p>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Reporting that matters</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Security metrics that give leadership genuine visibility, not a vulnerability count that means nothing without context. Risk reduction over time, remediation velocity, control coverage, and compliance posture. Reporting that informs decisions.
                </p>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Knowledge transfer built in</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Embedding is not about creating dependency. TechSonar engineers build your team's capability, secure coding practices, security champion programmes, and the internal knowledge to maintain your security posture independently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Ready to embed security into your engineering team?
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
